<template>
  <div>
    <ReportItemContainer v-if="isReportMode">
      <template #question>{{ question.text }}</template>
      <template #answer>
        <div
          v-if="showAnswer"
          v-for="(person, index) in persons"
          class="mb-3"
          :key="index"
        >
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("name") }}: </span
            >{{ person.name }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("surname") }}: </span
            >{{ person.surname }}
          </p>
          <p>
            <span class="report-item-container__label"
              >{{ getLabel("position") }}: </span
            >{{ person.position }}
          </p>
        </div>
      </template>
    </ReportItemContainer>

    <div v-else>
      <v-row>
        <v-col cols="12" sm="6" md="12">
          <h3 class="mb-2 mt-3">
            {{ question.text }}
            <InfoPopup
              v-if="question.helpText"
              :title="question.helpTitle ?? ''"
              :content="question.helpText"
            />
          </h3>
        </v-col>
      </v-row>
      <div v-for="(person, index) in persons" class="mb-8" :key="index">
        <v-divider v-if="index !== 0" class="mb-5"></v-divider>
        <v-row>
          <v-col cols="12" sm="6" md="6">
            <PInputField
              :label="getLabel('name')"
              :value="person.name"
              :readonly="isReadOnlyMode"
              @update-event="
                (val) => {
                  person.name = val;
                  emitNewPersons();
                }
              "
            />
          </v-col>
          <v-col cols="12" sm="6" md="6">
            <PInputField
              :label="getLabel('surname')"
              :value="person.surname"
              :readonly="isReadOnlyMode"
              @update-event="
                (val) => {
                  person.surname = val;
                  emitNewPersons();
                }
              "
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="6">
            <PInputField
              :label="getLabel('position')"
              :value="person.position"
              :readonly="isReadOnlyMode"
              @update-event="
                (val) => {
                  person.position = val;
                  emitNewPersons();
                }
              "
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pl-0">
            <v-btn
              size="medium"
              variant="plain"
              :disabled="isReadOnlyMode"
              @click="addPerson()"
              class="ma-2 mb-15"
              v-if="index == persons.length - 1"
            >
              <v-icon icon="mdi-plus"></v-icon>
              <u class="mb-1">{{ getLabel("addPerson") }}</u>
            </v-btn>
          </v-col>
          <v-col class="text-right">
            <v-btn
              size="medium"
              variant="plain"
              @click="removePerson(person)"
              class="ma-2 mb-15"
              v-if="persons.length > 1"
            >
              <v-icon icon="mdi-delete"></v-icon>
              <u class="mb-1">{{ getLabel("removePerson") }}</u>
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, type Ref } from "vue";
import ReportItemContainer from "@/components/containers/ReportItemContainer.vue";
import type { QuestionModelDto } from "@/models/dto/questionModelDto";
import InfoPopup from "@/components/custom/InfoPopup.vue";
import PInputField from "@/components/custom/PInputField.vue";
import { QuestionnaireTemplateCommonItemsId } from "@/models/enum/questionnaireTemplateCommonItemsId";
import { useSelectedQuestionnaire } from "@/stores/selectedQuestionnare";
import { personsWithNameAndPositionAnswerComplete } from "@/components/questionnaire/questionnaireUtils";
import {
  type AnswerPersonWithNameAndPosition,
  createEmptyPersonWithNameAndPosition,
  type PersonWithNameAndPosition,
} from "@/models/persons/personWithNameAndPosition";

const props = withDefaults(
  defineProps<{
    questionModel: QuestionModelDto;
    answer?: AnswerPersonWithNameAndPosition[] | null;
    isReportMode?: boolean;
    isReadOnlyMode?: boolean;
  }>(),
  {
    isReportMode: false,
    isReadOnlyMode: false,
  }
);

const emit = defineEmits<{
  (e: "qEvent", newValue: AnswerPersonWithNameAndPosition[]): void;
  (e: "isValid", isValid: boolean, invalidField: string): void;
}>();

const selectedQuestionnaireStore = useSelectedQuestionnaire();

const displayTexts = selectedQuestionnaireStore.getCommonItems(
  QuestionnaireTemplateCommonItemsId.PersonV2
);

const persons: Ref<PersonWithNameAndPosition[]> = ref(
  getPersonsWithNameAndPosition()
);
const question = computed(() => props.questionModel.question);
const showAnswer = computed(() =>
  personsWithNameAndPositionAnswerComplete(props.answer)
);

function emitNewPersons(): void {
  const answerPersons = persons.value.map((x) => x);
  emit("qEvent", answerPersons);
}

function emitValid(isValid: boolean, fieldId: string): void {
  emit("isValid", isValid, `${question.value.id}__${fieldId}`);
}

function addPerson(): void {
  persons.value.push(createEmptyPersonWithNameAndPosition());
}

function removePerson(item: PersonWithNameAndPosition): void {
  const index = persons.value.indexOf(item);
  persons.value.splice(index, 1);
  emitNewPersons();
}

function getPersonsWithNameAndPosition(): PersonWithNameAndPosition[] {
  if (!props.answer || props.answer.length === 0) {
    return [createEmptyPersonWithNameAndPosition()];
  }

  return props.answer.map((answerPerson: AnswerPersonWithNameAndPosition) => {
    return answerPerson as PersonWithNameAndPosition;
  });
}

function getLabel(id: string): string {
  return displayTexts.find((x) => x.id == id)?.text ?? "";
}
</script>
